import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIsMobile } from '@sravni/react-utils';
import { ControlGroup, Icon, Typography } from '@sravni/react-design-system';
import { Tooltip as TooltipIcon } from '@sravni/react-icons';
import { SelectValue } from '@sravni/react-design-system/dist/types/components/Select';
import classNames from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { amountTransform, Button, DataLabel, Paper, Select, TextInput, Tooltip } from '../../../../../landing.common';
import { scrollTo } from '../../../../../../helpers';
import { FORM_FIELDS } from '../../../../../../constants';
import { EventType } from '../../../../../../helpers/analytics';
import { CardsContext } from '../../../../context';
import { useWindowScroll } from '../../../../../../helpers/effects/useWindowScroll';
import { useFormData } from '../../../../../../store/selectors/formStore';
import { extendFormData } from '../../../../../../store/reducers/formStore';
import { useAnalytics } from '../../../../../hooks/analytics';
import { schema } from './schema';
import styles from './styles.module.scss';

export const CARD_PURPOSES = [
    { label: 'Покупки', value: 'goods' },
    { label: 'Снятие наличных', value: 'cash' },
    { label: 'Оплата кредитов', value: 'credits' },
    { label: 'Кэшбек и программы лояльности', value: 'cashback' },
    { label: 'Другое', value: 'other' },
];

interface Props {
    isMainFormVisible: boolean;
}

export const TopForm: React.FC<Props> = (props) => {
    const { topFormAdditionalData, setTopFormAdditionalData } = useContext(CardsContext);
    const { isMainFormVisible } = props;
    const dispatch = useDispatch();
    const formData = useFormData();
    const [isFirstLimitFetched, setIsFirstLimitFetched] = useState(false);
    const { pushEvent } = useAnalytics();

    const currentSchema = useRef(schema());
    const {
        control,
        reset,
        formState: { errors },
        getFieldState,
        getValues,
        watch,
        trigger,
    } = useForm({ resolver: yupResolver(currentSchema.current), mode: 'onBlur', criteriaMode: 'all' });

    const formAnchorRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const purposeOptions = CARD_PURPOSES;
    const monthlySalary = watch(FORM_FIELDS.MONTHLY_SALARY);

    useEffect(() => {
        reset(
            { [FORM_FIELDS.MONTHLY_SALARY]: formData[FORM_FIELDS.MONTHLY_SALARY] },
            { keepErrors: false, keepDirtyValues: true },
        );
    }, [formData, reset]);

    const [isTopFormVisible, setIsTopFormVisible] = useState(true);

    useWindowScroll(() => {
        if (formAnchorRef.current) {
            const { y: positionY } = formAnchorRef.current.getBoundingClientRect();
            if (positionY > 0) {
                setIsTopFormVisible(true);
            } else {
                setIsTopFormVisible(false);
            }
        }
    }, []);

    const isFormStuck = !isMainFormVisible && !isTopFormVisible && !isMobile;

    useEffect(() => {
        if (!isFirstLimitFetched && monthlySalary) {
            setIsFirstLimitFetched(true);
        }
    }, [monthlySalary, isFirstLimitFetched]);

    const updateData = (data: FE.FormData) => {
        dispatch(extendFormData(data));
        return null;
    };

    const onPurposeChange = (value?: SelectValue) => {
        setTopFormAdditionalData({ purpose: value as string });
        const purpose = CARD_PURPOSES.find((item) => item.value === value);
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Фильтр|Цель',
            eventLabel: purpose ? purpose.label : undefined,
        });
    };

    const formOnBlur: React.FocusEventHandler<HTMLFormElement> = async () => {
        await trigger(FORM_FIELDS.MONTHLY_SALARY);
        const value = getValues(FORM_FIELDS.MONTHLY_SALARY);
        const { error } = getFieldState(FORM_FIELDS.MONTHLY_SALARY);
        if (error) {
            return;
        }
        updateData({
            [FORM_FIELDS.MONTHLY_SALARY]: value,
        });
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Фильтр|Доход',
            eventLabel: value ? String(value) : 'Не указано',
        });
    };

    const onClickNext = () => {
        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Переход к анкете',
            eventLabel: isFormStuck ? 'Прилипала' : 'Зафиксировано',
        });
        const element = document.getElementById('main-form');
        if (element) {
            scrollTo(element);
        }
    };

    const tooltipText = () => (
        <>
            Для более точного расчёта необходимо заполнить ещё некоторые данные.
            <br />
            Это займёт около 5 минут
        </>
    );

    const formComponent = (
        <>
            <ControlGroup direction={isMobile ? 'vertical' : 'horizontal'}>
                <form className={styles.salaryInputContainer} onBlur={formOnBlur}>
                    <Controller
                        control={control}
                        name={FORM_FIELDS.MONTHLY_SALARY}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                label="Ежемесячный доход"
                                value={amountTransform.input(field)}
                                onChange={amountTransform.output(field)}
                                errorMessage={errors[FORM_FIELDS.MONTHLY_SALARY]?.message}
                                className={styles.salaryInput}
                            />
                        )}
                    />
                </form>
                <Select
                    options={purposeOptions}
                    label="Для чего вам нужна карта?"
                    value={topFormAdditionalData.purpose}
                    onChange={onPurposeChange}
                />
            </ControlGroup>

            <div className={styles.labels_button_container}>
                <div className={styles.labels}>
                    <DataLabel
                        valueClassName={styles.labelValue}
                        value={
                            <div>
                                <span>Низкая</span>
                                <Tooltip
                                    placement="top-start"
                                    withCrossIcon
                                    bubbleContentClassName={styles.bubbleContent}
                                    title={<Typography.Heading level={5}>Почему низкая?</Typography.Heading>}
                                    content={
                                        <div>
                                            {tooltipText()}
                                            <Button
                                                variant="text"
                                                color="blue"
                                                className={styles.tooltipButton}
                                                onClick={onClickNext}>
                                                Рассчитать точный лимит
                                            </Button>
                                        </div>
                                    }>
                                    <span>
                                        <Icon className={styles.icon}>
                                            <TooltipIcon />
                                        </Icon>
                                    </span>
                                </Tooltip>
                            </div>
                        }
                        caption="Точность расчета"
                    />
                </div>
                <Button className={styles.button} variant="primary" size={60} onClick={onClickNext}>
                    Рассчитать точный лимит
                </Button>
            </div>
        </>
    );

    return (
        <>
            <div ref={formAnchorRef} />
            <Paper className={classNames(styles.form)}>
                <div className={classNames(styles.form__content)}>{formComponent}</div>
            </Paper>
            <Paper className={classNames(styles.form, styles.form_stuck, { [styles.form_stuck__opened]: isFormStuck })}>
                <div className={classNames(styles.form__content, styles.form_stuck__content)}>{formComponent}</div>
            </Paper>
        </>
    );
};
